import { Inject } from '@odinjs/odin';

import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalButton, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { AdultTpnViewerAliases, FragmentNames } from '../../../../../codegen';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../../constants/routes';
import { YesNoType } from '../../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import ShowToast from '../../../../../utils/toast-service';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../../../commons/header/medication-and-date-header-params';
import { InfusionsViewerParams } from '../../../infusion-viewer/infusions-viewer-params';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../../services/timeline-service';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { AdminByProcessParams } from '../../medication-administration-by-process/medication-administration-by-process-params';
import { MedicationViewerParams } from '../../medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { AdultTpnType } from '../adult-tpn-types';
import { ItemStatus } from '../../../../../constants/utils-adep';
import { MedicationStatus } from '../../administration-schedule/administration-schedule-types';

@Controller({ component: AdultTpnViewerAliases.ADULT_TPN_VIEWER_LAYOUT, fragment: FragmentNames.ADULT_TPN_VIEWER })
export default class AdultTpnViewerController extends BaseController {

  @Inject(AdultTpnViewerAliases.ADULT_TPN_CONTENT_PANEL)
  private contentPanelApi!: PanelAPI;

  @Inject(AdultTpnViewerAliases.ADULT_TPN_FOOTER_PANEL)
  private footerPanelApi!: PanelAPI;

  @Inject(AdultTpnViewerAliases.EXECUTE_TIME_BUTTON)
  private executeTimeButtonApi!: ButtonAPI;

  @Inject(AdultTpnViewerAliases.INTERRUPT_BUTTON)
  private interruptButtonApi!: ButtonAPI;

  @OnMount(AdultTpnViewerAliases.ADULT_TPN_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;

    this.fetchAdultTpn().then((response) => {
      const adultTpn = response as AdultTpnType;

      MedicationViewerParams.adminSchedule = {
        dilutionDescription: '',
        materialSequence: timelineMedication.id,
        medicationHour: timelineMedication.start,
        prescriptionNumber: timelineMedication.prescriptionNumber,
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: timelineMedication.description,
        scheduleDate: timelineMedication.start,
        type: 'ADULT_PARENTERAL_NUTRITION'
      };

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        dripQuantity: adultTpn.dripQuantity,
        stageVolume: adultTpn.stageQuantity,
        totalVolume: adultTpn.totalVolume,
      }

      this.contentPanelApi.setVisible(true)
      this.footerPanelApi.setVisible(true)
      this.context.set({
        adultTpn: {
          ...adultTpn,
          itemStatus: timelineMedication.itemStatus,
          materialDescription: timelineMedication.description,
          materialSequence: timelineMedication.itemCode,
          scheduleDate: timelineMedication.start,
          scheduleSequence: timelineMedication.id,
          status: timelineMedication.status
        },
        encounterId,
        patientId,
      });
    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR)
      }
    })

  }

  @OnMount(AdultTpnViewerAliases.ADULT_TPN_DECK)
  handleMountDeck({ target }: EventData<DeckAPI>): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const adultTpn = this.context.get('adultTpn') as AdultTpnType

    target.setData([{
      description: timelineMedication.description,
      doseQuantity: timelineMedication.itemQuantity,
      infusionSpeed: adultTpn.dripQuantity,
      isChemotherapy: false,
      prescriptionId: timelineMedication.prescriptionNumber,
      prescriptionNumber: timelineMedication.prescriptionNumber,
      routeAdministrationDescription: adultTpn.administrationRoute,
      scheduleDate: timelineMedication.start,
      stageQuantity: adultTpn.stageQuantity,
      statusItem: timelineMedication.status,
      totalVolume: adultTpn.totalVolume
    }]);
  }

  @OnMount(AdultTpnViewerAliases.EXECUTE_TIME_BUTTON)
  handleMountInfusionStopButton(): void {
    this.validateAndSetButtonVisibility();
  }

  @OnMount([AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE, AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE])
  handleMountActionReverse(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    actionApi.setEnabled(this.shouldShowTerminateAction());
  }

  @OnClick(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleClickActionReverse(): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const itemType = PrescriptionType.ADULT_PARENTERAL_NUTRITION;
    const solutionType = 6;
    applicationContext.redirect(ApplicationRoute.ADEP_REVERSE_TPN, { encounterId, itemType, patientId, prescriptionNumber, sequenceNumber, solutionType });
  }

  @OnClick(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleClickTerminateAction(): void {
    const { patientId, encounterId, hourStatus, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate, itemStatus, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_ADULT_TPN_TERMINATE, { currentSpeedInfusion, encounterId, hourStatus, itemStatus, patientId, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate });
  }

  @OnMount(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleMountSuspendAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isAllowedToSuspend());
  }

  @OnClick(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleClickSuspendAction(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  private isStatusPending(): boolean {
    const { status } = TimelineContext.get();
    return [ItemStatus.PENDING, MedicationStatus.PENDING as string].includes(status)
  }

  private isAllowedToSuspend(): boolean {
    const allowSuspendTpn = 'S';
    const param668 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TPN_TO_BE_SUSPENDED);
    return this.isStatusPending() && param668 === allowSuspendTpn
  }

  @OnClick(AdultTpnViewerAliases.EXECUTE_TIME_BUTTON)
  handleClickExecuteTimeButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const startSolution = itemStatus === InfusionItemStatus.NOT_STARTED ? 'button.infusion-viewer.start' : 'button.infusion-viewer.restart-solution';
    const scheduleTitleDescription = MessageService.translate(startSolution);
    const adultTpn = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { adultTpn },
      focusOnOpen: false,
      fragment: FragmentNames.ADULT_TPN_START,
      id: FragmentNames.ADULT_TPN_START,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.ADULT_TPN_START),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(AdultTpnViewerAliases.INTERRUPT_BUTTON)
  handleMountInterruptButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);
    this.interruptButtonApi.setVisible(isValidItemStatusForInterrupt);
  }

  @OnClick(AdultTpnViewerAliases.INTERRUPT_BUTTON)
  handleClickInterruptButton(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    const interruptDescription = MessageService.translate(interruptExpression);
    const adultTpn = this.context.getBulkData() as unknown as AdultTpnType
    const dialogModal: BaseModalProps = {
      contextData: { adultTpn },
      focusOnOpen: false,
      fragment: FragmentNames.ADULT_TPN_INTERUPT,
      id: FragmentNames.ADULT_TPN_INTERUPT,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.ADULT_TPN_INTERUPT),
      title: interruptDescription,
      type: ModalType.DEFAULT,
    };
    AdminByProcessParams.admType = { isAdmProcess: false };
    ModalService.openDefault(dialogModal);
  }

  @OnMount(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleMountActionAdjustSpeed({ target }: EventData<ActionAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);

    target.setEnabled(isValidStatus);
  }

  @OnClick(AdultTpnViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleClickAdjustSpeedAction(): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const adultTpn = this.context.getBulkData() as unknown as AdultTpnType
    const itemType = 'NAN';

    InfusionsViewerParams.adminSchedule = {
      dosageTypeDescription: adultTpn.materialDescription
    };
    InfusionsViewerParams.infusionDetailsParams = {
      ...InfusionsViewerParams.infusionDetailsParams,
      itemType
    }
    InfusionsViewerParams.params = {
      currentSpeedInfusion: adultTpn.stepVolume ?? 0,
      establishmentCode: MedicineAndSolutionsParams.params.nrGroupingId,
      infusionName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(prescriptionNumber),
      solutionNumber: String(sequenceNumber),
    }

    applicationContext.redirect(ApplicationRoute.ADEP_ADJUST_INFUSION_SPEED, {
      encounterId,
      patientId,
    });
  }

  private fetchAdultTpn(): Promise<AdultTpnType> {
    const { itemSequence } = TimelineContext.get() as TimeLineEventMedication;

    return HttpService.get<AdultTpnType>(Endpoint.EMAR_GET_ADULT_TPN_INFORMATION, {
      scheduleSequenceNumber: itemSequence,
    }).then((response) => {
      const responseBody = response.body as AdultTpnType;
      this.context.set({ ...responseBody });
      return responseBody;
    })
  }

  private validateAndSetButtonVisibility(): void {
    const { hourStatus, itemStatus } = this.historyService.getCurrentRouteParams() as { hourStatus: string, itemStatus: string };
    const isValidItemStatusForAdministered = [InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.T].some(status => status === itemStatus);
    const isNotStarted = hourStatus === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.RESTARTED

    if ((hourStatus === InfusionHourStatus.ADMINISTERED && isValidItemStatusForAdministered) || isNotStarted) {
      this.executeTimeButtonApi.setVisible(false);
      return;
    }

    if (this.shouldStartAdultTpn(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.START_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    if (this.shouldRestartAdultTpn(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.RESTART_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    this.executeTimeButtonApi.setVisible(false);
  }

  private shouldStartAdultTpn(itemStatus: string, status: string): boolean {
    const isItemStatusValid = itemStatus === InfusionItemStatus.NOT_STARTED;
    const isStatusValid = status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION;
    return isItemStatusValid && isStatusValid
  }

  private shouldRestartAdultTpn(itemStatus: string, status: string): boolean {
    const isInterrupted = this.isInterrupted(itemStatus) && status === InfusionHourStatus.INTERRUPTED;
    const isPending = itemStatus === InfusionItemStatus.INTERRUPTED && status === InfusionHourStatus.PENDING;
    return isInterrupted || isPending;
  }

  private setScreenLabel(actionType: string, status: string): string {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isInterrupted = this.isInterrupted(itemStatus)

    if (
      actionType !== ActionType.START_SOLUTION ||
      (status === InfusionHourStatus.INTERRUPTED && isInterrupted) ||
      (status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.INTERRUPTED)
    ) {
      return 'button.infusion-viewer.restart'
    }

    if (status !== InfusionHourStatus.STARTED) {
      return 'text.start-solution-by-schedule'
    }

    return ''
  }

  private setExecuteTimeButton(label: string): void {
    this.executeTimeButtonApi.setValue(MessageService.translate(label));
    this.executeTimeButtonApi.setVisible(true);
  }

  private isInterrupted(itemStatus: string): boolean {
    return [InfusionItemStatus.II.valueOf(), InfusionItemStatus.INTERRUPTED.valueOf()].includes(itemStatus)
  }

  private shouldShowTerminateAction(): boolean {
    const param27 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR)
    if (param27 === YesNoType.YES) {
      return false
    }
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.II].some(st => st === itemStatus);
    return isValidStatus
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    const noCardsRead = AdminByScheduleParams.readParam?.noCardsRead;

    if (noCardsRead) {
      ModalService.close(id);
      ModalService.close('dialog-modal-service');
      return;
    }

    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }
}
